import { useEffect, useState } from 'react';
import DateField from '../components/DateField.jsx';
import { api, withQuery, money, date, dateTime, isoDate } from '../api.js';
import { useAuth } from '../App.jsx';

/**
 * Каса.
 *
 * Наличността се смята от движенията, не се пази като число. Затова
 * единственият начин да се промени е чрез движение — включително
 * разликата, установена при засичане.
 */
export default function Cash() {
  const { user } = useAuth();
  // Оператор поправя само днешни записи (20.10.2026) — същото пази и сървърът.
  const mozheStari = ['admin', 'manager'].includes(user?.role);
  const dnesKasa = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Sofia' }).format(new Date());
  const mozhePopravka = (den) => mozheStari || String(den ?? '').slice(0, 10) >= dnesKasa;
  const [registers, setRegisters] = useState([]);
  const [selected, setSelected] = useState(null);
  /*
   * Книгата тръгва от последния месец, не от днешния ден.
   *
   * Ръчните движения са редки — аванс, бензин, внасяне в банката — и за
   * един ден често няма нито едно. Отворена на днес, страницата изглежда
   * празна и човек трябва да пипа датите, за да види има ли изобщо нещо.
   */
  const [range, setRange] = useState(() => {
    const dnes = new Date();
    const predi = new Date(dnes.getTime() - 30 * 86400000);
    return { from: isoDate(predi), to: isoDate(dnes) };
  });
  /*
   * И показва само ръчните.
   *
   * Плащанията по продажби са десетки на ден и затрупват онова, което
   * човек търси тук: какво е правено с парите извън търговията.
   */
  const [only, setOnly] = useState('manual');
  const [book, setBook] = useState(null);
  // Поправяното ръчно движение от касовата книга (07.10.2026).
  const [popravka, setPopravka] = useState(null);
  // Засичане, което се поправя (09.10.2026) — формата горе се пълни с него.
  const [popravkaZasichane, setPopravkaZasichane] = useState(null);
  // Разгърнатите редове на книгата — по подразбиране всеки е на един ред.
  const [razgarnati, setRazgarnati] = useState(() => new Set());
  const razgarni = (k) => setRazgarnati((st) => {
    const n = new Set(st); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });
  const [counts, setCounts] = useState([]);
  const [pending, setPending] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const canWrite = user.role !== 'auditor';

  async function loadRegisters() {
    const res = await api.get('/api/cash/balance');
    const vidimi = res.registers.filter((r) => Number(r.on_cash_page) === 1);
    setRegisters(vidimi);
    setSelected((s) => s ?? vidimi[0]?.id ?? null);
  }

  useEffect(() => {
    loadRegisters().catch((e) => setError(e.message));
    api.get('/api/cash/fiscal-queue').then(setPending).catch(() => {});
    api.get('/api/cash/categories').then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(withQuery('/api/cash/movements', { cashRegisterId: selected, ...range, only }))
      .then(setBook)
      .catch((e) => setError(e.message));
    api.get(withQuery('/api/cash/counts', { cashRegisterId: selected, limit: 30 }))
      .then(setCounts)
      .catch(() => {});
  }, [selected, range, only]);

  async function refresh() {
    await loadRegisters();
    const res = await api.get(
      withQuery('/api/cash/movements', { cashRegisterId: selected, ...range, only }));
    setBook(res);
    setCounts(await api.get(withQuery('/api/cash/counts', { cashRegisterId: selected, limit: 30 })));
    setPending(await api.get('/api/cash/fiscal-queue'));
  }

  /*
   * „По програма“ винаги текущо (26.10.2026): страницата стоеше отворена от
   * засичането, продажбите в брой след това не влизаха в сумата, а
   * преброените пари — да; разликата „растеше“ (96,81 → 179,81 €). Опреснява
   * се на 20 сек. и при връщане към раздела; въведеното във формата остава.
   */
  useEffect(() => {
    if (!selected) return undefined;
    const t = setInterval(() => { refresh().catch(() => {}); }, 20000);
    const f = () => { refresh().catch(() => {}); };
    window.addEventListener('focus', f);
    return () => { clearInterval(t); window.removeEventListener('focus', f); };
  }, [selected, range, only]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = registers.find((r) => r.id === selected);

  return (
    <>
      <div className="page-head">
        <h1>Каса</h1>
        <div className="spacer" />
        {pending.length > 0 && (
          <span className="badge warn" title="Чакат фискалното устройство да бъде свързано">
            {pending.length} чакащи команди към ФУ
          </span>
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {notice && <div className="notice">{notice}</div>}

      <div className="tiles">
        {registers.map((r) => (
          <button
            key={r.id}
            className="tile"
            onClick={() => setSelected(r.id)}
            style={{
              textAlign: 'left',
              cursor: 'pointer',
              borderColor: r.id === selected ? 'var(--accent)' : undefined,
              borderWidth: r.id === selected ? 2 : 1,
            }}
          >
            <div className="label">{r.name}</div>
            <div className="value">{money(r.balance, r.currency)}</div>
            <div className="sub">
              {r.kind === 'cash'
                ? (r.lastCount ? `засечена на ${date(r.lastCount.count_date)}` : 'още не е засичана')
                : 'банкова сметка'}
            </div>
          </button>
        ))}
      </div>

      {current && (
        <>
          {canWrite && current.kind === 'cash' && (
            <div className="card">
              <div className="card-head">Ежедневно</div>
              <div className="card-body">
                <DailyRoutine
                  register={current}
                  balance={current.balance}
                  onDone={async (msg) => { setNotice(msg); await refresh(); }}
                  onError={setError}
                />
              </div>
            </div>
          )}

          {canWrite && (
            <div className="card">
              <div className="card-head">Внасяне и изваждане — {current.name}</div>
              <div className="card-body">
                <MovementForm
                  register={current}
                  categories={categories}
                  onDone={async (msg) => { setNotice(msg); await refresh(); }}
                  onError={setError}
                />
              </div>
            </div>
          )}

          {canWrite && registers.filter((r) => Number(r.in_transfers) === 1).length > 1 && (
            <div className="card">
              <div className="card-head">Прехвърляне между каса и банка</div>
              <div className="card-body">
                <TransferForm
                  registers={registers}
                  onDone={async (msg) => { setNotice(msg); await refresh(); }}
                  onError={setError}
                />
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-head">
              Касова книга
              <div style={{ flex: 1 }} />
              {/* Ръчните са малко и се четат; плащанията по продажби ги
                  затрупват. Затова книгата тръгва само с ръчните. */}
              <label className="check" style={{ marginRight: 10 }}>
                <input type="checkbox" checked={only === 'manual'}
                  onChange={(e) => setOnly(e.target.checked ? 'manual' : 'all')} />
                само ръчните
              </label>
              <DateField value={range.from}
                onChange={(v) => setRange((r) => ({ ...r, from: v }))} />
              <DateField value={range.to}
                onChange={(v) => setRange((r) => ({ ...r, to: v }))} />
            </div>

            {!book ? <div className="empty">Зареждане…</div> : (
              <>
                <div className="table-wrap">
                  <table className="compact">
                    <thead>
                      {/*
                        * Сумите стоят до основанието, не в другия край.
                        *
                        * Окото минава от „за какво" направо към „колко";
                        * между тях нямаше какво да стои. „Документ" отпадна
                        * — при ръчните движения номерът им не казва нищо, а
                        * потребителят отиде накрая, където се поглежда рядко.
                        */}
                      <tr>
                        <th>Дата</th>
                        <th>Основание</th>
                        <th className="num">Приход</th>
                        <th className="num">Разход</th>
                        <th>Потребител</th>
                        {book.only !== 'manual' && <th className="num">Салдо</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {book.only !== 'manual' && (
                        <tr>
                          <td colSpan={5} className="muted">Салдо в началото на периода</td>
                          <td className="num">
                            <strong>{money(book.opening, current.currency)}</strong>
                          </td>
                        </tr>
                      )}
                      {book.movements.map((m, i) => (
                        <tr key={i}>
                          <td>{dateTime(m.at)}</td>
                          <td className="kniga-osnovanie">
                            {/* Един ред (07.10.2026): дългото се реже с „…“; клик
                                върху текста го разгръща, втори клик — свива. */}
                            <div className="kniga-red">
                              <span
                                title={[m.reason, m.note].filter(Boolean).join(' · ')}
                                onClick={() => razgarni(m.ref ?? i)}
                                style={{
                                  cursor: 'pointer', minWidth: 0, flex: '0 1 auto',
                                  ...(razgarnati.has(m.ref ?? i)
                                    ? { whiteSpace: 'normal' }
                                    : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                                }}>
                                {m.reason}
                                {m.note && <span className="muted" style={{ fontSize: 12 }}> · {m.note}</span>}
                              </span>
                              {/* Перото стои до основанието: по него се сумира. */}
                              {m.category && <span className="badge kniga-etiket">{m.category}</span>}
                              {m.source === 'manual' && <span className="badge muted kniga-etiket">ръчно</span>}
                              {m.source === 'manual' && canWrite && m.movement_id && mozhePopravka(m.d) && (
                                <button type="button" className="link kniga-popravi" title="поправи"
                                  onClick={() => setPopravka(m)}>
                                  <span className="kniga-popravi-dalgo">поправи</span>
                                  <span className="kniga-popravi-kratko">✎</span>
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="num" style={{ color: 'var(--ok)' }}>
                            {m.direction === 'in'
                              ? <strong>{money(m.amount, current.currency)}</strong> : ''}
                          </td>
                          <td className="num" style={{ color: 'var(--danger)' }}>
                            {m.direction === 'out'
                              ? <strong>{money(m.amount, current.currency)}</strong> : ''}
                          </td>
                          <td className="muted">{m.who ?? '—'}</td>
                          {book.only !== 'manual' && (
                            <td className="num muted">
                              {m.balanceAfter === null ? '' : money(m.balanceAfter, current.currency)}
                            </td>
                          )}
                        </tr>
                      ))}
                      {book.movements.length === 0 && (
                        <tr>
                          <td colSpan={book.only === 'manual' ? 5 : 6} className="empty">
                            Няма движения за периода
                          </td>
                        </tr>
                      )}
                      {/*
                        * При само ръчните вместо салдо се дават сборовете.
                        *
                        * Салдото на касата се получава от всичко, което
                        * влиза и излиза; показано под филтриран списък, то
                        * би изглеждало като сбор на видимите редове и щеше
                        * да лъже.
                        */}
                      {book.only === 'manual' ? (
                        <tr className="sbor-red">
                          <td colSpan={2}>Сбор на показаните</td>
                          <td className="num" style={{ color: 'var(--ok)' }}>
                            <strong>{money(book.totalIn, current.currency)}</strong>
                          </td>
                          <td className="num" style={{ color: 'var(--danger)' }}>
                            <strong>{money(book.totalOut, current.currency)}</strong>
                          </td>
                          <td />
                        </tr>
                      ) : (
                        <tr className="sbor-red">
                          <td colSpan={5}>Салдо в края на периода</td>
                          <td className="num">
                            <strong>{money(book.closing, current.currency)}</strong>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {popravka && (
            <PopravkaModal
              m={popravka}
              categories={categories}
              currency={current.currency}
              onClose={() => setPopravka(null)}
              onDone={async (msg) => { setPopravka(null); setNotice(msg); await refresh(); }}
            />
          )}

          {/*
            * Засичането е броене на банкноти и монети — по банкова сметка
            * няма какво да се брои. Салдото ѝ идва от извлечението, не от
            * чекмеджето.
            */}
          {canWrite && current.kind === 'cash' && (
            <div className="card">
              <div className="card-head" id="zasichane">
                {popravkaZasichane
                  ? `Поправка на засичането от ${date(popravkaZasichane.count_date)}`
                  : 'Засичане на касата'}
              </div>
              <div className="card-body">
                <CountForm
                  key={popravkaZasichane?.id ?? 'novo'}
                  register={current}
                  edit={popravkaZasichane}
                  expected={popravkaZasichane
                    ? Number(popravkaZasichane.expected_amount)
                    : (book?.only === 'manual' ? current.balance : book?.closing)}
                  onDone={async (msg) => { setPopravkaZasichane(null); setNotice(msg); await refresh(); }}
                  onCancel={() => setPopravkaZasichane(null)}
                  onError={setError}
                />
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-head">Последни засичания</div>
            <table className="compact">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th className="num">По програма</th>
                  <th className="num">Преброено</th>
                  <th className="num">Разлика</th>
                  <th className="num">По устройство</th>
                  <th>Засякъл</th>
                  <th>Забележка</th>
                  {canWrite && <th />}
                </tr>
              </thead>
              <tbody>
                {counts.map((c) => {
                  const diff = Number(c.difference);
                  return (
                    <tr key={c.id}>
                      <td>{date(c.count_date)}</td>
                      <td className="num muted">{money(c.expected_amount)}</td>
                      <td className="num">{money(c.counted_amount)}</td>
                      <td className="num" style={{
                        color: Math.abs(diff) < 0.005 ? 'var(--ok)' : 'var(--danger)',
                      }}>
                        {Math.abs(diff) < 0.005 ? 'няма' : money(c.difference)}
                      </td>
                      <td className="num muted">
                        {c.device_amount != null ? money(c.device_amount) : '—'}
                      </td>
                      <td className="muted">
                        {c.created_by_name ?? '—'}
                        {c.edited_at && (
                          <div style={{ fontSize: 11 }} title={`поправено ${c.edited_by_name ?? ''}`}>
                            поправено{c.edited_by_name ? ` · ${c.edited_by_name}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="muted">{c.note ?? '—'}</td>
                      {canWrite && (
                        <td>
                          {mozhePopravka(c.count_date) && <button type="button" className="link" onClick={() => {
                            setPopravkaZasichane(c);
                            document.getElementById('zasichane')?.scrollIntoView({ behavior: 'smooth' });
                          }}>поправи</button>}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {counts.length === 0 && (
                  <tr><td colSpan={canWrite ? 8 : 7} className="empty">Още няма засичания</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/**
 * Двете операции, които се правят всеки ден.
 *
 * Вечер парите се изваждат и касата остава на нула; към фискалното
 * устройство не се праща нищо, защото дневният отчет го занулява.
 * Сутрин наличните пари се внасят обратно и това се обявява на
 * устройството като служебно въведени, за да тръгне денят точно.
 */
function DailyRoutine({ register, balance, onDone, onError }) {
  const [morningAmount, setMorningAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const current = Number(balance ?? 0);

  // Сутрин полето се пълни със сумата от последното вечерно извеждане;
  // след като днес вече е внесено, остава празно (02.10.2026).
  useEffect(() => {
    let zhivo = true;
    api.get(`/api/cash/registers/${register.id}/morning-hint`)
      .then((h) => { if (zhivo && h.amount != null) setMorningAmount(String(Number(h.amount))); })
      .catch(() => {});
    return () => { zhivo = false; };
  }, [register.id]);

  async function closeDay() {
    if (current <= 0) return;
    if (!window.confirm(`Изваждане на цялата наличност ${money(current)} и зануляване на касата?`)) {
      return;
    }
    setBusy(true);
    try {
      /*
       * Сумата не се подава оттук — сървърът я смята сам, точно преди да
       * я извади. Ако този екран е стоял отворен от по-рано (например от
       * вчера) и `current` вече не отговаря на истинската наличност,
       * подадено число би извадило повече или по-малко, отколкото трябва,
       * и касата не би излязла на нула. Веднъж точно това се случи —
       * извади се сума, каквато касата отдавна нямаше.
       */
      await api.post(`/api/cash/registers/${register.id}/empty`, {});
      onDone('Касата е занулена за деня.');
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function openDay() {
    setBusy(true);
    try {
      await api.post('/api/cash/movements', {
        cashRegisterId: register.id,
        direction: 'in',
        amount: Number(morningAmount),
        reason: 'Сутрешно въвеждане',
        sendToFiscalDevice: true,
      });
      setMorningAmount('');
      onDone('Началната сума е въведена и обявена като служебно въведени.');
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="toolbar" style={{ marginBottom: 0, alignItems: 'flex-end' }}>
      <div className="field">
        Сутрешно въвеждане
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="number" step="0.01" min="0" autoComplete="off"
            value={morningAmount}
            onChange={(e) => setMorningAmount(e.target.value)}
            placeholder="начална сума"
            style={{ width: 130 }}
          />
          <button className="primary" onClick={openDay}
            disabled={busy || !(Number(morningAmount) > 0)}>
            Внеси
          </button>
        </div>
        <span className="hint">издава се бон за служебно въведени</span>
      </div>

      <div style={{ width: 30 }} />

      <div className="field">
        Вечерно извеждане
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input value={money(current, register.currency)} disabled style={{ width: 130 }} />
          <button onClick={closeDay} disabled={busy || current <= 0}>
            Извади всичко
          </button>
        </div>
        <span className="hint">без бон — дневният отчет занулява апарата</span>
      </div>
    </div>
  );
}

/**
 * Поправка или изтриване на ръчно движение (07.10.2026).
 *
 * Основание/перо/бележка — на място. Сума или дата — сторно на стария
 * запис и нов; изтриване — само сторно. „И към апарата“ праща разликата
 * като служебно въведени/изведени — по подразбиране включено, освен за
 * вечерното извеждане и корекциите на книгата, които не минават през апарата.
 */
function PopravkaModal({ m, categories, currency, onClose, onDone }) {
  const [reason, setReason] = useState(m.reason ?? '');
  const [note, setNote] = useState(m.note ?? '');
  const [categoryId, setCategoryId] = useState(m.category_id ? String(m.category_id) : '');
  const [amount, setAmount] = useState(String(Number(m.amount)));
  const [den, setDen] = useState(String(m.d).slice(0, 10));
  const bezAparat = /^(Вечерно извеждане|Корекция на касовата книга)/.test(m.reason ?? '');
  const [toDevice, setToDevice] = useState(!bezAparat);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const promenenaSuma = Math.round(Number(amount) * 100) !== Math.round(Number(m.amount) * 100);
  const promenenaData = den !== String(m.d).slice(0, 10);
  const parite = promenenaSuma || promenenaData;
  const vidimi = categories.filter((c) => c.direction === 'both' || c.direction === m.direction);

  async function zapishi() {
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/api/cash/movements/${m.movement_id}/correct`, {
        reason: reason.trim(), note: note.trim() || null,
        categoryId: categoryId ? Number(categoryId) : null,
        amount: Number(amount), movementDate: den, toDevice: parite && toDevice,
      });
      await onDone(r.mode === 'text' ? 'Движението е поправено.' : 'Движението е поправено (сторно + нов запис).');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  async function iztrii() {
    if (!window.confirm(`Да се изтрие „${m.reason}“ за ${money(m.amount, currency)}?`)) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/api/cash/movements/${m.movement_id}/delete`, { toDevice });
      await onDone('Движението е изтрито (сторно).');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 560 }}>
        <div className="card-head">
          Поправка: {m.direction === 'in' ? 'приход' : 'разход'} {money(m.amount, currency)}
        </div>
        <div className="card-body">
          {error && <div className="error" style={{ marginBottom: 10 }}>{error}</div>}
          <label className="field">
            Основание
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <label className="field" style={{ flex: 1 }}>
              Перо
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">без перо</option>
                {vidimi.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="field" style={{ width: 130 }}>
              Сума
              <input type="number" step="0.01" min="0" value={amount}
                onChange={(e) => setAmount(e.target.value)} />
            </label>
            <div className="field" style={{ width: 170 }}>
              Дата
              <DateField value={den} onChange={(v) => setDen(v)} />
            </div>
          </div>
          <label className="field" style={{ marginTop: 10 }}>
            Забележка
            <input value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <label className="check" style={{ marginTop: 12 }}>
            <input type="checkbox" checked={toDevice} onChange={(e) => setToDevice(e.target.checked)} />
            и към касовия апарат (служебно въведени/изведени за разликата)
          </label>
          <p className="hint" style={{ marginBottom: 0 }}>
            {parite
              ? 'Сума или дата: старият запис се сторнира и се записва нов — в книгата остава само новият.'
              : 'Само текст — поправя се на място, без да пипа парите.'}
          </p>
        </div>
        <div className="modal-foot">
          <button className="danger" onClick={iztrii} disabled={busy} style={{ marginRight: 'auto' }}>
            Изтрий
          </button>
          <button onClick={onClose} disabled={busy}>Откажи</button>
          <button className="primary" onClick={zapishi}
            disabled={busy || reason.trim().length < 2 || !(Number(amount) > 0)}>
            Запиши
          </button>
        </div>
      </div>
    </div>
  );
}

function MovementForm({ register, categories = [], onDone, onError }) {
  const [direction, setDirection] = useState('out');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  /*
   * Перото носи основанието.
   *
   * „Разходи офис" се събира в справка само защото е перо; какво точно е
   * купено стои в бележката. Затова при избор на перо основанието се
   * попълва с името му — операторът може да го допише, но не е длъжен да
   * измисля текст всеки път.
   */
  const vidimi = categories.filter((c) => c.direction === 'both' || c.direction === direction);
  const izberi = (id) => {
    setCategoryId(id);
    const c = categories.find((x) => String(x.id) === String(id));
    if (c && !reason.trim()) setReason(c.name);
  };

  async function submit() {
    setBusy(true);
    try {
      await api.post('/api/cash/movements', {
        cashRegisterId: register.id,
        direction,
        amount: Number(amount),
        reason: reason.trim(),
        categoryId: categoryId ? Number(categoryId) : null,
        note: note.trim() || null,
      });
      setAmount(''); setReason(''); setNote(''); setCategoryId('');
      onDone(
        direction === 'in'
          ? 'Сумата е внесена в касата.'
          : 'Сумата е извадена от касата.'
      );
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const ready = Number(amount) > 0 && reason.trim().length >= 2;

  return (
    <div className="toolbar" style={{ marginBottom: 0, alignItems: 'flex-end' }}>
      <label className="field">
        Посока
        <select value={direction} onChange={(e) => setDirection(e.target.value)}>
          <option value="out">изваждане (разход)</option>
          <option value="in">внасяне (приход)</option>
        </select>
      </label>
      <label className="field">
        Перо
        <select value={categoryId} onChange={(e) => izberi(e.target.value)}
          style={{ minWidth: 190 }}>
          <option value="">без перо</option>
          {vidimi.map((c) => (
            <option key={c.id} value={c.id}>{c.code} · {c.name}</option>
          ))}
        </select>
      </label>
      <label className="field">
        Сума
        <input type="number" step="0.01" min="0" value={amount}
          onChange={(e) => setAmount(e.target.value)} style={{ width: 120 }} />
      </label>
      <label className="field" style={{ flex: 1, minWidth: 220 }}>
        Основание
        <input value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="напр. плащане на доставчик, гориво, внасяне в банка" />
      </label>
      <label className="field" style={{ flex: 1, minWidth: 180 }}>
        Забележка
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <button className="primary" onClick={submit} disabled={!ready || busy}>
        {busy ? 'Записване…' : 'Запиши'}
      </button>
      {register.kind === 'cash' && (
        <span className="hint" style={{ alignSelf: 'center', maxWidth: 240 }}>
          Изпраща се и към фискалното устройство като служебно
          {direction === 'in' ? ' въведени' : ' изведени'}.
        </span>
      )}
    </div>
  );
}

/** Евровите номинали, от най-едрия надолу. */
const NOTES = ['500', '200', '100', '50', '20', '10', '5'];
const COINS = ['2', '1', '0.50', '0.20', '0.10', '0.05', '0.02', '0.01'];

/**
 * Обичайното дребно, което стои в касата. Предварително попълнено, за да
 * не се брои всеки ден наново — променя се, ако е различно.
 */
const DEFAULT_COINS = { '0.01': '25', '0.02': '25', '0.05': '55' };

/**
 * Прехвърляне между каса и банка.
 *
 * Обичайното е в една посока: събрали са се пари в касата, някой ги
 * взима и ги внася по сметката. Затова формата тръгва оттам — от касата
 * в брой към първата банкова сметка — а обратното се прави с копчето за
 * размяна.
 *
 * Записват се две движения: излизане от едната и влизане в другата.
 * Парите не изчезват, а сменят мястото си, и двете страни трябва да го
 * показват.
 */
function TransferForm({ registers, onDone, onError }) {
  /*
   * В списъците стоят само местата, между които наистина се носят пари.
   *
   * „Банка ПОС" и „ППП" не са такива: там сумите пристигат сами по начина
   * на плащане и никой не ги внася на ръка. Кои участват се казва от
   * самата каса (`in_transfers`), не оттук — утре може да се отвори втора
   * сметка, без да се пипа програмата.
   */
  const uchastvat = registers.filter((r) => Number(r.in_transfers) === 1);
  const kasi = uchastvat.filter((r) => r.kind === 'cash');
  const banki = uchastvat.filter((r) => r.kind !== 'cash');
  // Обичайното: от чекмеджето към сметката.
  const [from, setFrom] = useState(() => String((kasi[0] ?? uchastvat[0])?.id ?? ''));
  const [to, setTo] = useState(() => String((banki[0] ?? uchastvat[1])?.id ?? ''));
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const otKasa = uchastvat.find((r) => String(r.id) === String(from));
  const kamKasa = uchastvat.find((r) => String(r.id) === String(to));
  const ready = Number(amount) > 0 && from && to && from !== to;

  async function submit() {
    setBusy(true);
    try {
      await api.post('/api/cash/transfer', {
        fromRegisterId: Number(from),
        toRegisterId: Number(to),
        amount: Number(amount),
        note: note.trim() || null,
      });
      setAmount(''); setNote('');
      onDone(`${money(amount)} минаха от „${otKasa?.name}" в „${kamKasa?.name}".`);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="toolbar" style={{ marginBottom: 0, alignItems: 'flex-end' }}>
      <label className="field">
        От
        <select value={from} onChange={(e) => setFrom(e.target.value)}>
          {uchastvat.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </label>
      <button title="размени посоката" style={{ marginBottom: 1 }}
        onClick={() => { const a = from; setFrom(to); setTo(a); }}>⇄</button>
      <label className="field">
        В
        <select value={to} onChange={(e) => setTo(e.target.value)}>
          {uchastvat.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </label>
      <label className="field">
        Сума
        <input type="number" step="0.01" min="0" value={amount}
          onChange={(e) => setAmount(e.target.value)} style={{ width: 120 }} />
      </label>
      <label className="field" style={{ flex: 1, minWidth: 200 }}>
        Забележка
        <input value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="кой ги внесе, номер на вносна бележка" />
      </label>
      <button className="primary" onClick={submit} disabled={!ready || busy}>
        {busy ? 'Записване…' : 'Прехвърли'}
      </button>
      {otKasa?.kind === 'cash' && (
        <span className="hint" style={{ alignSelf: 'center', maxWidth: 230 }}>
          Изпраща се и към фискалното устройство като служебно извеждане.
        </span>
      )}
    </div>
  );
}

/*
 * Чернова на засичането (20.10.2026). Операторът брои купюрите, отива на
 * друг екран и при връщане всичко беше изчезнало. Започнатото (само ново
 * засичане, не поправка) се пази в браузъра по каса и по ден; маха се при
 * „Засечи касата“ и „Изчисти“, а на следващия ден не се зарежда.
 */
const dnesLokalno = () => new Intl.DateTimeFormat('sv-SE').format(new Date());
const klyuchChernova = (registerId) => `tefter.cashCountDraft.${registerId}`;
function prochetiChernovaZasichane(registerId) {
  try {
    const d = JSON.parse(localStorage.getItem(klyuchChernova(registerId)) ?? 'null');
    return d && d.den === dnesLokalno() ? d : null;
  } catch { return null; }
}
function iztriiChernovaZasichane(registerId) {
  try { localStorage.removeItem(klyuchChernova(registerId)); } catch { /* */ }
}

function CountForm({ register, expected, edit = null, onDone, onCancel, onError }) {
  const chernova = edit ? null : prochetiChernovaZasichane(register?.id);
  // При поправка формата тръгва от записаното, не от празно.
  const zapisani = (() => {
    if (!edit?.denominations) return null;
    const d = typeof edit.denominations === 'string' ? JSON.parse(edit.denominations) : edit.denominations;
    return Object.fromEntries(Object.entries(d ?? {}).map(([k, v]) => [k, String(v)]));
  })();
  const [counts, setCounts] = useState(edit ? (zapisani ?? {}) : (chernova?.counts ?? DEFAULT_COINS));
  const [otherAmount, setOtherAmount] = useState(
    edit && Number(edit.other_amount) ? String(Number(edit.other_amount)) : (chernova?.otherAmount ?? ''));
  const [otherNote, setOtherNote] = useState(edit?.other_note ?? chernova?.otherNote ?? '');
  const [deviceAmount, setDeviceAmount] = useState(
    edit?.device_amount != null ? String(Number(edit.device_amount)) : (chernova?.deviceAmount ?? ''));
  const [note, setNote] = useState(edit?.note ?? chernova?.note ?? '');

  // Всяка промяна по новото засичане отива в черновата.
  useEffect(() => {
    if (edit || !register?.id) return;
    try {
      localStorage.setItem(klyuchChernova(register.id), JSON.stringify({
        den: dnesLokalno(), counts, otherAmount, otherNote, deviceAmount, note,
      }));
    } catch { /* без localStorage — просто не се пази */ }
  }, [edit, register?.id, counts, otherAmount, otherNote, deviceAmount, note]);
  const [busy, setBusy] = useState(false);
  // Грешката стои до копчето — горе на страницата не се вижда, докато се
  // брои долу, и изглеждаше, че „нищо не става".
  const [localError, setLocalError] = useState(null);

  const notesTotal = [...NOTES, ...COINS].reduce(
    (sum, d) => sum + Math.round(Number(d) * 100) * (Number(counts[d]) || 0),
    0
  ) / 100;
  const other = Number(otherAmount) || 0;
  const counted = Math.round((notesTotal + other) * 100) / 100;

  const anyEntered = Object.values(counts).some((v) => Number(v) > 0) || other !== 0;
  const diff = counted - Number(expected ?? 0);

  function set(d, value) {
    setCounts((c) => ({ ...c, [d]: value.replace(/[^\d]/g, '') }));
  }

  async function submit() {
    setBusy(true);
    setLocalError(null);
    try {
      const denominations = {};
      for (const [d, v] of Object.entries(counts)) {
        const n = Number(v) || 0;
        if (n > 0) denominations[d] = n;
      }
      const telo = {
        denominations,
        countedAmount: counted,
        otherAmount: other,
        otherNote: otherNote.trim() || null,
        deviceAmount: deviceAmount === '' ? null : Number(deviceAmount),
        note: note.trim() || null,
      };
      const res = edit
        ? await api.put(`/api/cash/counts/${edit.id}`, telo)
        : await api.post('/api/cash/count', { cashRegisterId: register.id, ...telo });
      setCounts(DEFAULT_COINS);
      setOtherAmount(''); setOtherNote(''); setDeviceAmount(''); setNote('');
      if (!edit) iztriiChernovaZasichane(register.id);
      const d = Number(res.difference);
      if (edit) {
        onDone(Math.abs(d) < 0.005
          ? 'Засичането е поправено — няма разлика.'
          : `Засичането е поправено. Разлика ${money(res.difference)}.`);
        return;
      }
      onDone(
        Math.abs(d) < 0.005
          ? 'Касата е засечена — няма разлика.'
          : `Касата е засечена. Разлика ${money(res.difference)} — потърсете грешката и засечете отново, или коригирайте касата ръчно през внасяне/изваждане.`
      );
    } catch (err) {
      setLocalError(err.message);
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
        <DenominationColumn title="Банкноти" items={NOTES} counts={counts} onChange={set} obshto="Общо банкноти" />
        <DenominationColumn title="Монети" items={COINS} counts={counts} onChange={set} />

        <div style={{ minWidth: 260, flex: 1 }}>
          <div className="field" style={{ marginBottom: 14 }}>
            Други
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="number" step="0.01" autoComplete="off"
                value={otherAmount}
                onChange={(e) => setOtherAmount(e.target.value)}
                placeholder="0.00"
                style={{ width: 110 }}
              />
              <input
                value={otherNote}
                onChange={(e) => setOtherNote(e.target.value)}
                placeholder="какво е"
                style={{ flex: 1 }}
              />
            </div>
            <span className="hint">може и с минус — влиза в преброената сума</span>
          </div>

          <div className="totals" style={{ marginLeft: 0, width: '100%' }}>
            <div><span className="muted">По програма</span><span>{money(expected ?? 0)}</span></div>
            <div><span className="muted">Купюри</span><span>{money(notesTotal)}</span></div>
            {other !== 0 && (
              <div><span className="muted">Други</span><span>{money(other)}</span></div>
            )}
            <div><span className="muted">Преброено</span><span>{money(counted)}</span></div>
            <div className="grand">
              <span>Разлика</span>
              <span style={{ color: Math.abs(diff) < 0.005 ? 'var(--ok)' : 'var(--danger)' }}>
                {Math.abs(diff) < 0.005 ? 'няма' : money(diff)}
              </span>
            </div>
          </div>

          <label className="field" style={{ marginTop: 14 }}>
            По фискалното устройство
            <input type="number" step="0.01" min="0" value={deviceAmount} autoComplete="off"
              onChange={(e) => setDeviceAmount(e.target.value)} />
            <span className="hint">по избор — за сверка с наличността в апарата</span>
          </label>

          <label className="field" style={{ marginTop: 10 }}>
            Забележка
            <input value={note} onChange={(e) => setNote(e.target.value)} />
          </label>

          <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
            <button className="primary" onClick={submit} disabled={!anyEntered || busy}>
              {busy ? 'Записване…' : (edit ? 'Запиши поправката' : 'Засечи касата')}
            </button>
            {edit && <button onClick={onCancel} disabled={busy}>Откажи</button>}
            {localError && <span className="error" style={{ margin: 0 }}>{localError}</span>}
            {anyEntered && (
              <button
                onClick={() => {
                  setCounts(DEFAULT_COINS); setOtherAmount(''); setOtherNote('');
                  setDeviceAmount(''); setNote('');
                  if (!edit) iztriiChernovaZasichane(register.id);
                }}
                disabled={busy}
              >Изчисти</button>
            )}
          </div>

          {anyEntered && Math.abs(diff) >= 0.005 && (
            <p className="hint" style={{ marginTop: 10 }}>
              {diff > 0
                ? `Излишък ${money(diff)} спрямо очакваното.`
                : `Липса ${money(Math.abs(diff))} спрямо очакваното.`}
              {' '}Само за сведение — касата не се променя автоматично.
            </p>
          )}
          <p className="hint" style={{ marginTop: 8 }}>
            Може да засичате касата колкото пъти искате през деня. Засичането е само констатация и не пипа
            истинската каса — при реална разлика я коригирайте през внасяне/изваждане.
          </p>
        </div>
      </div>
    </>
  );
}

function DenominationColumn({ title, items, counts, onChange, obshto }) {
  // Сборът на колоната (14.10.2026) — засега само под банкнотите.
  const sborCenti = items.reduce(
    (s, d) => s + Math.round(Number(d) * 100) * (Number(counts[d]) || 0), 0);
  return (
    <div>
      <div className="k" style={{ marginBottom: 8, fontWeight: 600 }}>{title}</div>
      <table style={{ width: 230 }}>
        <tbody>
          {items.map((d) => {
            const n = Number(counts[d]) || 0;
            const sum = (Math.round(Number(d) * 100) * n) / 100;
            return (
              <tr key={d}>
                <td style={{ width: 62, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {/* Всичко в евро — „ст" е за стотинки, а вече не работим с лев. */}
                  {Number(d) >= 1 ? `${d} €` : `${Number(d).toLocaleString('bg-BG', { minimumFractionDigits: 2 })} €`}
                </td>
                <td style={{ width: 74 }}>
                  <input
                    className="num"
                    inputMode="numeric"
                    autoComplete="off"
                    value={counts[d] ?? ''}
                    onChange={(e) => onChange(d, e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td className="num muted">{n > 0 ? money(sum) : ''}</td>
              </tr>
            );
          })}
        </tbody>
        {obshto && (
          <tfoot>
            <tr>
              <td colSpan={2} style={{
                textAlign: 'right', fontWeight: 600, paddingTop: 18, borderTop: '2px solid var(--border)',
              }}>{obshto}</td>
              <td className="num" style={{
                fontWeight: 700, paddingTop: 18, borderTop: '2px solid var(--border)',
              }}>{money(sborCenti / 100)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
